import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import "../styles_1/FlashcardGenerator.css"; // Ensure styles are imported

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const [view, setView] = useState<"login" | "signup" | "reset">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const { login, signup, resetPassword } = useAuth();

  if (!isOpen) return null;

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { email?: string; password?: string } = {};

    if (!email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(email)) newErrors.email = "Invalid email format";

    if (view !== "reset") {
      if (!password.trim()) newErrors.password = "Password is required";
      else if (view === "signup" && password.length < 6)
        newErrors.password = "Password must be 6+ chars";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setGeneralError("");
    setResetSent(false);

    try {
      if (view === "login") {
        await login(email, password);
        onClose();
      } else if (view === "signup") {
        await signup(email, password);
        onClose();
      } else if (view === "reset") {
        await resetPassword(email);
        setResetSent(true);
      }
    } catch (err: any) {
      console.error(err);
      if (
        err.code === "auth/invalid-credential" ||
        err.code === "auth/user-not-found" ||
        err.code === "auth/wrong-password"
      ) {
        setGeneralError("Invalid email or password.");
      } else if (err.code === "auth/email-already-in-use") {
        setGeneralError("Email is already registered.");
      } else {
        setGeneralError("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Inline Styles
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(4px)",
    zIndex: 2000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const contentStyle: React.CSSProperties = {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "30px",
    width: "90%",
    maxWidth: "400px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    position: "relative",
  };

  return (
    <div className="modal-overlay" style={overlayStyle} onClick={onClose}>
      <div
        className="modal-content"
        style={contentStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "none",
            border: "none",
            fontSize: "1.5rem",
            cursor: "pointer",
            color: "#94a3b8",
          }}
        >
          ×
        </button>

        <div
          className="modal-header"
          style={{ textAlign: "center", marginBottom: "24px" }}
        >
          <h2 style={{ margin: "0 0 8px 0", color: "#1e293b" }}>
            {view === "login" && "Welcome Back"}
            {view === "signup" && "Join ScoreSmart"}
            {view === "reset" && "Reset Password"}
          </h2>
          <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>
            {view === "login" && "Sign in to access your library"}
            {view === "signup" && "Create an account to save your progress"}
            {view === "reset" && "Enter your email to receive a reset link"}
          </p>
        </div>

        {generalError && (
          <div
            style={{
              background: "#fee2e2",
              color: "#dc2626",
              padding: "10px",
              borderRadius: "8px",
              fontSize: "0.9rem",
              marginBottom: "16px",
              textAlign: "center",
            }}
          >
            {generalError}
          </div>
        )}

        {/* SUCCESS MESSAGE FOR RESET - UPDATED */}
        {view === "reset" && resetSent ? (
          <div
            style={{
              background: "#f0fdf4",
              color: "#166534",
              padding: "15px",
              borderRadius: "8px",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>Check your inbox!</p>
            <p style={{ margin: "5px 0 0 0", fontSize: "0.9rem" }}>
              We've sent a password reset link to <strong>{email}</strong>.
            </p>

            {/* NEW SPAM WARNING */}
            <p
              style={{
                margin: "12px 0 0 0",
                fontSize: "0.85rem",
                color: "#b45309",
                background: "#fffbeb",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #fcd34d",
                fontWeight: 500,
              }}
            >
              ⚠️ If you don't see it, please check your{" "}
              <strong>Spam/Junk</strong> folder.
            </p>

            <button
              onClick={() => setView("login")}
              style={{
                marginTop: "15px",
                background: "none",
                border: "none",
                color: "#166534",
                textDecoration: "underline",
                cursor: "pointer",
                fontSize: "0.9rem",
              }}
            >
              Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group" style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#334155",
                  marginBottom: "6px",
                }}
              >
                Email Address
              </label>
              <input
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors({ ...errors, email: undefined });
                }}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: errors.email
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  fontSize: "1rem",
                }}
              />
              {errors.email && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "0.8rem",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  {errors.email}
                </span>
              )}
            </div>

            {view !== "reset" && (
              <div className="form-group" style={{ marginBottom: "24px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "6px",
                  }}
                >
                  <label
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#334155",
                    }}
                  >
                    Password
                  </label>
                  {view === "login" && (
                    <button
                      type="button"
                      onClick={() => {
                        setView("reset");
                        setGeneralError("");
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#8b5cf6",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                        fontWeight: 500,
                      }}
                    >
                      Forgot Password?
                    </button>
                  )}
                </div>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors({ ...errors, password: undefined });
                  }}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: errors.password
                      ? "1px solid #ef4444"
                      : "1px solid #cbd5e1",
                    fontSize: "1rem",
                  }}
                />
                {errors.password && (
                  <span
                    style={{
                      color: "#ef4444",
                      fontSize: "0.8rem",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {errors.password}
                  </span>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#8b5cf6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: loading ? "wait" : "pointer",
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading
                ? "Processing..."
                : view === "login"
                  ? "Sign In"
                  : view === "signup"
                    ? "Create Account"
                    : "Send Reset Link"}
            </button>
          </form>
        )}

        {!resetSent && (
          <div
            className="modal-footer"
            style={{
              marginTop: "20px",
              textAlign: "center",
              fontSize: "0.9rem",
              color: "#64748b",
            }}
          >
            {view === "login" && (
              <p>
                New here?{" "}
                <button
                  onClick={() => {
                    setView("signup");
                    setGeneralError("");
                  }}
                  style={linkBtnStyle}
                >
                  Create Account
                </button>
              </p>
            )}
            {view === "signup" && (
              <p>
                Already have an account?{" "}
                <button
                  onClick={() => {
                    setView("login");
                    setGeneralError("");
                  }}
                  style={linkBtnStyle}
                >
                  Sign In
                </button>
              </p>
            )}
            {view === "reset" && (
              <p>
                <button
                  onClick={() => {
                    setView("login");
                    setGeneralError("");
                  }}
                  style={linkBtnStyle}
                >
                  Back to Sign In
                </button>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const linkBtnStyle = {
  background: "none",
  border: "none",
  color: "#7c3aed",
  fontWeight: 700,
  cursor: "pointer",
  textDecoration: "underline",
};
